import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import {
  Tooltip,
  Button,
  Modal,
  Form,
  DatePicker,
  Row,
  Col,
  Select,
  message,
  Card,
} from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import { IContract } from '@components/DeviceActionModal/types';
import useSwr from '@hooks/useSwr';
import { useNavigate } from 'react-router-dom';
import SelectCustomers from '@components/SelectCustomers';

const RequestCancellationReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('reports');
  const { selectedApp, isMot, isRoot, theme } = useContext(SessionContext);

  const [form] = Form.useForm();
  const customerId = Form.useWatch('customerId', form);
  const [isRequesting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data: contractsData } = useSwr<IContract[] | ''>(
    isModalVisible ? '/service-proxy/contracts' : null,
    {
      params: {
        customerId,
        product: selectedApp?.toUpperCase(),
      },
    },
  );

  const contractsOptions = useMemo(
    () =>
      Array.isArray(contractsData)
        ? contractsData?.map(({ assetNumber, planName, nickname }) => ({
            value: assetNumber,
            label: `${assetNumber}${
              nickname ? ` - ${nickname}` : ''
            } - ${planName}`,
          }))
        : [],
    [contractsData],
  );

  const handleClose = useCallback(() => {
    setIsModalVisible(false);
    form.resetFields();
  }, [form]);

  const navigateToReports = useCallback(
    (type: string) => {
      message.destroy('report-requested');
      navigate(`/support/reports?type=${type}`);
    },
    [navigate],
  );

  const handleRequest = useCallback(async () => {
    try {
      const payload = {
        startDate: form.getFieldValue('dateRange')[0].format('YYYY-MM-DD'),
        endDate: form.getFieldValue('dateRange')[1].format('YYYY-MM-DD'),
        contractAssetNumber: form.getFieldValue('contractAssetNumber'),
      };

      let product = selectedApp?.replace('_', '')?.toUpperCase();
      if (isMot) {
        product = 'MOT';
      }

      await api.get(`/service-proxy/inventory/${product}/canceled-devices`, {
        params: payload,
      });

      message.success({
        content: (
          <span>
            {t('request.report-success')}
            <Button
              onClick={() => navigateToReports('CANCELED_INVENTORY')}
              type="link"
              style={{ padding: 3 }}
            >
              {t('request.go-to-reports')}
            </Button>
            {t('request.check-report-progress-and-download')}
          </span>
        ),
        key: 'report-requested',
        duration: 5,
      });
      handleClose();
    } catch {
      message.error(t('request.report-error'));
    }
  }, [form, handleClose, isMot, navigateToReports, selectedApp, t]);

  const disabledFutureDates = useCallback(
    (current: any) => !!current && current > moment().endOf('day'),
    [],
  );

  const renderContract = (
    <Col span={24}>
      <Form.Item
        label={t('attributes:contract')}
        name="contractAssetNumber"
        validateStatus={contractsData === '' ? 'error' : undefined}
        help={
          contractsData === '' ? (
            <span style={{ color: theme.colors.red }}>
              {t('messages.no-contracts')}
            </span>
          ) : undefined
        }
        style={{ marginBottom: isRoot ? 8 : 0 }}
      >
        <Select
          allowClear
          showSearch
          optionFilterProp="value"
          options={contractsOptions}
          loading={contractsData === undefined}
          disabled={contractsData === '' || contractsOptions?.length === 0}
        />
      </Form.Item>
    </Col>
  );

  return (
    <>
      <Tooltip title={t('request.cancellation-report')}>
        <Button
          icon={<FileExcelOutlined />}
          disabled={isRequesting}
          loading={isRequesting}
          onClick={() => setIsModalVisible(true)}
          className="report-request-button"
        />
      </Tooltip>

      <Modal
        title={t('request.cancellation-report')}
        open={isModalVisible}
        onCancel={handleClose}
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={isRequesting}
            onClick={() => form.submit()}
          >
            {t('request.buttons.confirm')}
          </Button>,
        ]}
        width={480}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRequest}
          initialValues={{
            dateRange: [moment().subtract(1, 'days'), moment()],
          }}
          style={{ padding: 16 }}
        >
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item label={t('request.date')} name="dateRange">
                <DatePicker.RangePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  allowClear={false}
                  defaultPickerValue={[moment().subtract(1, 'month'), moment()]}
                  disabledDate={disabledFutureDates}
                />
              </Form.Item>
            </Col>
            {isRoot ? (
              <Card
                style={{
                  marginTop: 8,
                  width: '100%',
                  background: theme?.colors?.background1,
                }}
              >
                <Col span={24}>
                  <Form.Item label={t('attributes:customer')} name="customerId">
                    <SelectCustomers
                      onChange={() => {
                        form.setFieldValue('contractAssetNumber', undefined);
                      }}
                    />
                  </Form.Item>
                </Col>
                {renderContract}
              </Card>
            ) : (
              renderContract
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default memo(RequestCancellationReport);
